import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, CheckIcon, ShieldCheckIcon, UsersIcon, BrainIcon, WrenchIcon, RefreshCwIcon, HeadphonesIcon, ClockIcon, FileTextIcon, SettingsIcon, TrendingUpIcon, BuildingIcon, ZapIcon, LayersIcon, LockIcon } from 'lucide-react';
import { CTABanner } from '../../components/CTABanner';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5
    }
  }
};
const stagger = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.1
    }
  }
};
function SectionDivider() {
  return <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/2 h-px bg-gradient-to-r from-transparent via-violet-electric/20 to-transparent" data-id="element-1289" />;
}
export function EnterprisePage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <div className="min-h-screen w-full bg-navy" data-id="element-1290">
      {/* ── SECTION 1: HERO ── */}
      <section className="relative w-full min-h-screen flex items-center justify-center pt-32 pb-24 px-6 overflow-hidden bg-navy" data-id="element-1291">
        <div className="absolute inset-0 pointer-events-none" style={{
        background: 'radial-gradient(ellipse 80% 60% at 50% 0%, rgba(108,92,231,0.18) 0%, rgba(56,103,214,0.08) 50%, transparent 80%)'
      }} data-id="element-1292" />
        <div className="absolute bottom-0 left-0 right-0 h-32 pointer-events-none" style={{
        background: 'linear-gradient(to top, #0D0B2E, transparent)'
      }} data-id="element-1293" />

        <div className="relative z-10 max-w-3xl mx-auto text-center" data-id="element-1294">
          <motion.span className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-blue-400/30 bg-blue-400/10 text-blue-400 text-xs font-semibold tracking-[0.15em] uppercase mb-6" initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.4,
          delay: 0.1
        }} data-id="element-1295">
            Thruline Enterprise
          </motion.span>

          <motion.h1 className="text-5xl sm:text-6xl md:text-7xl font-bold text-white leading-[1.05] tracking-tight mb-6" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.55,
          delay: 0.2
        }} data-id="element-1296">
            Your custom ERP.
            <br data-id="element-1297" />
            <span className="text-transparent bg-clip-text" style={{
            backgroundImage: 'linear-gradient(90deg, #60a5fa, #818cf8)'
          }} data-id="element-1298">
              Built and run by experts.
            </span>
          </motion.h1>

          <motion.p className="text-lg sm:text-xl text-white/55 max-w-2xl mx-auto mb-10 leading-relaxed" initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.3
        }} data-id="element-1299">
            Thruline Enterprise is a fully custom ERP, configured to your
            actual operations and maintained by Thruline's team. We handle the
            system. You handle the business.
          </motion.p>

          <motion.div className="flex flex-col sm:flex-row items-center justify-center gap-4" initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.4
        }} data-id="element-1300">
            <a href="https://calendly.com/alex-thruline/learn-about-thruline" className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-white text-navy font-semibold text-sm hover:bg-white/90 transition-all hover:shadow-lg hover:shadow-white/10" data-id="element-1301">
              Get a Demo
              <ArrowRightIcon className="w-4 h-4" data-id="element-1302" />
            </a>
            <a href="https://calendly.com/alex-thruline/learn-about-thruline" className="inline-flex items-center px-8 py-3.5 rounded-full border border-white/25 text-white font-medium text-sm hover:border-white/50 transition-all" data-id="element-1303">
              Talk to our team
            </a>
          </motion.div>
        </div>
      </section>

      {/* ── SECTION 2: THE PROMISE ── */}
      <section className="relative w-full bg-navy-light py-24 px-6" data-id="element-1304">
        <SectionDivider data-id="element-1305" />
        <div className="max-w-3xl mx-auto" data-id="element-1306">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-60px'
        }} variants={stagger} data-id="element-1307">
            <motion.span variants={fadeUp} className="text-violet-electric text-xs font-semibold tracking-[0.2em] uppercase block mb-4" data-id="element-1308">
              The Promise
            </motion.span>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-bold text-white tracking-tight mb-12" data-id="element-1309">
              A system built around your operation.
              <br data-id="element-1310" />
              Not the other way around.
            </motion.h2>

            <div className="space-y-8" data-id="element-1311">
              <motion.div variants={fadeUp} className="flex gap-5" data-id="element-1312">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-500/10 border border-blue-500/20 flex items-center justify-center mt-0.5" data-id="element-1313">
                  <BrainIcon className="w-4 h-4 text-blue-400" data-id="element-1314" />
                </div>
                <div data-id="element-1315">
                  <p className="text-white/70 text-base leading-relaxed" data-id="element-1316">
                    <span className="text-white font-medium" data-id="element-1317">
                      Your business has its own way of working.
                    </span>{' '}
                    Your receiving process, your production scheduling, your
                    quality checks — they exist because they work for your
                    operation. An ERP should reflect that reality, not replace
                    it with someone else's template.
                  </p>
                </div>
              </motion.div>

              <motion.div variants={fadeUp} className="flex gap-5" data-id="element-1318">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-violet-500/10 border border-violet-500/20 flex items-center justify-center mt-0.5" data-id="element-1319">
                  <UsersIcon className="w-4 h-4 text-violet-400" data-id="element-1320" />
                </div>
                <div data-id="element-1321">
                  <p className="text-white/70 text-base leading-relaxed" data-id="element-1322">
                    <span className="text-white font-medium" data-id="element-1323">
                      But building a custom system takes a team most
                      manufacturers don't have.
                    </span>{' '}
                    Enterprise architects, implementation consultants, ongoing
                    developers — the cost and complexity of standing up that
                    capability internally puts truly custom ERP out of reach.
                  </p>
                </div>
              </motion.div>

              <motion.div variants={fadeUp} className="flex gap-5" data-id="element-1324">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mt-0.5" data-id="element-1325">
                  <ShieldCheckIcon className="w-4 h-4 text-emerald-400" data-id="element-1326" />
                </div>
                <div data-id="element-1327">
                  <p className="text-white/70 text-base leading-relaxed" data-id="element-1328">
                    <span className="text-white font-medium" data-id="element-1329">
                      Thruline Enterprise is that team.
                    </span>{' '}
                    We discover how your operation actually works, build a system
                    that matches, and keep it running and evolving as your
                    business grows. You focus on your business. We focus on your
                    system.
                  </p>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── SECTION 3: HOW IT WORKS ── */}
      <section className="relative w-full bg-navy py-24 px-6" data-id="element-1330">
        <SectionDivider data-id="element-1331" />
        <div className="max-w-4xl mx-auto" data-id="element-1332">
          <motion.div className="text-center mb-16" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-60px'
        }} variants={stagger} data-id="element-1333">
            <motion.span variants={fadeUp} className="text-violet-electric text-xs font-semibold tracking-[0.2em] uppercase block mb-4" data-id="element-1334">
              How It Works
            </motion.span>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-bold text-white tracking-tight" data-id="element-1335">
              We discover it. We build it. We run it.
            </motion.h2>
          </motion.div>

          <motion.div className="space-y-5" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-40px'
        }} variants={stagger} data-id="element-1336">
            {/* Phase 1 */}
            <motion.div variants={fadeUp} className="rounded-2xl border border-white/[0.06] bg-white/[0.02] overflow-hidden" data-id="element-1337">
              <div className="flex items-start gap-6 p-8" data-id="element-1338">
                <div className="flex-shrink-0" data-id="element-1339">
                  <div className="w-12 h-12 rounded-xl bg-violet-electric/15 border border-violet-electric/20 flex items-center justify-center" data-id="element-1340">
                    <BrainIcon className="w-6 h-6 text-violet-electric" data-id="element-1341" />
                  </div>
                </div>
                <div className="flex-1" data-id="element-1342">
                  <div className="flex items-center gap-3 mb-3" data-id="element-1343">
                    <span className="text-violet-electric text-xs font-bold tracking-widest uppercase" data-id="element-1344">
                      Phase 1
                    </span>
                    <span className="text-white/20 text-xs" data-id="element-1345">&mdash;</span>
                    <span className="text-white font-semibold" data-id="element-1346">Discovery</span>
                    <span className="ml-auto px-2.5 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-[10px] font-bold tracking-wide uppercase" data-id="element-1347">
                      Free
                    </span>
                  </div>
                  <p className="text-white/55 text-sm leading-relaxed mb-4" data-id="element-1348">
                    Thruline's AI reads your existing documents — SOPs,
                    spreadsheets, emails, PDFs — and maps your actual
                    operational processes. Our team reviews the results and
                    designs the system architecture: what your ERP needs to do,
                    how the modules connect, and why.
                  </p>
                  <p className="text-white/40 text-sm" data-id="element-1349">
                    <span className="text-white/60 font-medium" data-id="element-1350">
                      What you get:
                    </span>{' '}
                    A complete picture of your operation translated into system
                    architecture — without months of consultant interviews.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Phase 2 */}
            <motion.div variants={fadeUp} className="rounded-2xl border border-white/[0.06] bg-white/[0.02] overflow-hidden" data-id="element-1351">
              <div className="flex items-start gap-6 p-8" data-id="element-1352">
                <div className="flex-shrink-0" data-id="element-1353">
                  <div className="w-12 h-12 rounded-xl bg-violet-electric/15 border border-violet-electric/20 flex items-center justify-center" data-id="element-1354">
                    <WrenchIcon className="w-6 h-6 text-violet-electric" data-id="element-1355" />
                  </div>
                </div>
                <div className="flex-1" data-id="element-1356">
                  <div className="flex items-center gap-3 mb-3" data-id="element-1357">
                    <span className="text-violet-electric text-xs font-bold tracking-widest uppercase" data-id="element-1358">
                      Phase 2
                    </span>
                    <span className="text-white/20 text-xs" data-id="element-1359">&mdash;</span>
                    <span className="text-white font-semibold" data-id="element-1360">
                      Implementation
                    </span>
                    <span className="ml-auto px-2.5 py-0.5 rounded-full bg-violet-electric/10 border border-violet-electric/20 text-violet-electric text-[10px] font-bold tracking-wide uppercase" data-id="element-1361">
                      Fixed Quote
                    </span>
                  </div>
                  <p className="text-white/55 text-sm leading-relaxed mb-4" data-id="element-1362">
                    Thruline builds your complete system. We handle architecture
                    decisions, workflow design, integrations, testing, and
                    deployment. You participate in review checkpoints to
                    validate that the system matches your operation — but the
                    build is on us.
                  </p>
                  <p className="text-white/40 text-sm" data-id="element-1363">
                    <span className="text-white/60 font-medium" data-id="element-1364">
                      What you get:
                    </span>{' '}
                    A production-ready ERP configured to your actual processes,
                    delivered at a fixed price scoped after Discovery.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Phase 3 */}
            <motion.div variants={fadeUp} className="rounded-2xl border border-blue-400/15 bg-blue-400/[0.03] overflow-hidden" data-id="element-1365">
              <div className="flex items-start gap-6 p-8" data-id="element-1366">
                <div className="flex-shrink-0" data-id="element-1367">
                  <div className="w-12 h-12 rounded-xl bg-blue-400/20 border border-blue-400/30 flex items-center justify-center" data-id="element-1368">
                    <HeadphonesIcon className="w-6 h-6 text-blue-400" data-id="element-1369" />
                  </div>
                </div>
                <div className="flex-1" data-id="element-1370">
                  <div className="flex items-center gap-3 mb-3" data-id="element-1371">
                    <span className="text-blue-400 text-xs font-bold tracking-widest uppercase" data-id="element-1372">
                      Phase 3
                    </span>
                    <span className="text-white/20 text-xs" data-id="element-1373">&mdash;</span>
                    <span className="text-white font-semibold" data-id="element-1374">Operate</span>
                    <span className="ml-auto px-2.5 py-0.5 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-[10px] font-bold tracking-wide uppercase" data-id="element-1375">
                      Managed Service
                    </span>
                  </div>
                  <p className="text-white/55 text-sm leading-relaxed mb-4" data-id="element-1376">
                    You use your ERP to run your business. Thruline manages
                    everything behind the scenes: updates, system health,
                    change requests, and ongoing optimization. When your
                    business changes, you tell us what's different — we adjust
                    the system to match.
                  </p>
                  <p className="text-white/40 text-sm" data-id="element-1377">
                    <span className="text-white/60 font-medium" data-id="element-1378">
                      What you get:
                    </span>{' '}
                    A system that stays aligned with your operation without
                    requiring internal technical staff to maintain it.
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── SECTION 4: WHO IT'S FOR ── */}
      <section className="relative w-full bg-navy-light py-24 px-6" data-id="element-1379">
        <SectionDivider data-id="element-1380" />
        <div className="max-w-3xl mx-auto" data-id="element-1381">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-60px'
        }} variants={stagger} data-id="element-1382">
            <motion.span variants={fadeUp} className="text-violet-electric text-xs font-semibold tracking-[0.2em] uppercase block mb-4" data-id="element-1383">
              Who It's For
            </motion.span>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-bold text-white tracking-tight mb-4" data-id="element-1384">
              For companies that want a custom ERP without the custom ERP
              headache.
            </motion.h2>
            <motion.p variants={fadeUp} className="text-white/50 text-base mb-10" data-id="element-1385">
              Thruline Enterprise is for you if:
            </motion.p>

            <motion.div variants={stagger} className="space-y-4" data-id="element-1386">
              {['You want a fully custom ERP without any involvement in the technical build', 'You prefer to hand the system to an expert team and focus entirely on running your business', 'You have the budget for a managed service and value speed over hands-on control', 'You need complex integrations, compliance requirements, or multi-site deployments that benefit from dedicated expert management', "You don't want to invest time learning system configuration — you want it done"].map((item, i) => <motion.div key={i} variants={fadeUp} className="flex items-start gap-4" data-id="element-1387">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-400/15 border border-blue-400/25 flex items-center justify-center mt-0.5" data-id="element-1388">
                    <CheckIcon className="w-3.5 h-3.5 text-blue-400" data-id="element-1389" />
                  </div>
                  <p className="text-white/70 text-base leading-relaxed" data-id="element-1390">
                    {item}
                  </p>
                </motion.div>)}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── SECTION 5: WHAT'S INCLUDED ── */}
      <section className="relative w-full bg-navy py-24 px-6" data-id="element-1391">
        <SectionDivider data-id="element-1392" />
        <div className="max-w-5xl mx-auto" data-id="element-1393">
          <motion.div className="text-center mb-16" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-60px'
        }} variants={stagger} data-id="element-1394">
            <motion.span variants={fadeUp} className="text-violet-electric text-xs font-semibold tracking-[0.2em] uppercase block mb-4" data-id="element-1395">
              What's Included
            </motion.span>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-bold text-white tracking-tight" data-id="element-1396">
              Everything built, managed, and maintained.
            </motion.h2>
          </motion.div>

          <motion.div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-40px'
        }} variants={stagger} data-id="element-1397">
            {[{
            icon: LayersIcon,
            title: 'Full Module Suite',
            desc: 'Production, inventory, purchasing, shipping, quality, field operations — every module your operation needs, configured and integrated by Thruline.'
          }, {
            icon: WrenchIcon,
            title: 'Thruline-Built Configuration',
            desc: 'Every workflow, rule, and automation is designed and built by Thruline\'s team based on your actual operational processes. No templates. No compromises.'
          }, {
            icon: HeadphonesIcon,
            title: 'Managed Operations',
            desc: 'System updates, performance monitoring, change requests, and ongoing optimization — all handled by Thruline. Your team uses the system; we keep it running.'
          }, {
            icon: RefreshCwIcon,
            title: 'Continuous Alignment',
            desc: 'When your business changes, the system changes with it. New product lines, new processes, new requirements — tell Thruline, and we adjust.'
          }, {
            icon: SettingsIcon,
            title: 'Integrations & Compliance',
            desc: 'Thruline handles integrations with your existing tools, compliance requirements, and multi-site coordination. Complex technical work stays off your plate.'
          }, {
            icon: FileTextIcon,
            title: 'Full Documentation',
            desc: 'Your system is fully documented and traceable. Every configuration decision links back to the operational process that informed it. Maintained by Thruline.'
          }, {
            icon: ShieldCheckIcon,
            title: 'Expert Quality Control',
            desc: 'Every change goes through Thruline\'s review process. Architecture decisions, workflow modifications, and system updates are validated by experts before deployment.'
          }, {
            icon: TrendingUpIcon,
            title: 'Expansion on Demand',
            desc: 'Ready for new modules or major system expansion? Same process — Discovery, fixed quote, Thruline builds it. Grows with your business without growing your team.'
          }, {
            icon: ClockIcon,
            title: 'Fast Delivery',
            desc: 'Thruline\'s AI-powered discovery and implementation process delivers production-ready systems in weeks, not the months or years typical of traditional ERP.'
          }].map((item, i) => {
            const IconComp = item.icon;
            return <motion.div key={i} variants={fadeUp} className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-7" data-id="element-1398">
                  <div className="w-9 h-9 rounded-lg bg-blue-400/12 flex items-center justify-center mb-4" data-id="element-1399">
                    <IconComp className="w-4.5 h-4.5 text-blue-400" data-id="element-1400" />
                  </div>
                  <h3 className="text-sm font-semibold text-white mb-2" data-id="element-1401">
                    {item.title}
                  </h3>
                  <p className="text-white/45 text-sm leading-relaxed" data-id="element-1402">
                    {item.desc}
                  </p>
                </motion.div>;
          })}
          </motion.div>
        </div>
      </section>

      {/* ── SECTION 6: WHY ENTERPRISE ── */}
      <section className="relative w-full bg-navy-light py-24 px-6" data-id="element-1403">
        <SectionDivider data-id="element-1404" />
        <div className="max-w-4xl mx-auto" data-id="element-1405">
          <motion.div className="text-center mb-16" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-60px'
        }} variants={stagger} data-id="element-1406">
            <motion.span variants={fadeUp} className="text-violet-electric text-xs font-semibold tracking-[0.2em] uppercase block mb-4" data-id="element-1407">
              Common Questions
            </motion.span>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-bold text-white tracking-tight" data-id="element-1408">
              Why Thruline Enterprise
            </motion.h2>
          </motion.div>

          <motion.div className="grid grid-cols-1 md:grid-cols-2 gap-5" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-40px'
        }} variants={stagger} data-id="element-1409">
            {[{
            q: 'How is this different from hiring an ERP consultant?',
            a: "Traditional consultants interview your team for months, configure a system based on their interpretation, and hand you the keys. Thruline's AI reads your actual documents to understand your operation — no interpretation gap. Then we stay. We don't hand you the keys and leave. We operate and maintain the system as a managed service."
          }, {
            q: 'What if our business changes after go-live?',
            a: "That's expected. When your operation changes — new product line, new process, new supplier — you tell Thruline what's different. We scope the change, quote it if it's significant, and implement it. Standard adjustments are included in your managed service. Your system stays aligned with your business."
          }, {
            q: 'How long does implementation take?',
            a: 'Thruline\'s AI-powered discovery and build process is dramatically faster than traditional ERP implementation. Discovery takes days, not months. The full build depends on scope, but most initial implementations are measured in weeks. You\'ll get a specific timeline after Discovery.'
          }, {
            q: 'What if we want more control later?',
            a: 'Thruline also offers Core — a self-evolving platform where your team takes on day-to-day configuration with Thruline advisory support. Enterprise customers can transition to Core if they develop the internal capability and desire for more hands-on control. The two products complement each other.'
          }].map((item, i) => <motion.div key={i} variants={fadeUp} className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-8" data-id="element-1410">
                <h3 className="text-base font-semibold text-white mb-3 leading-snug" data-id="element-1411">
                  "{item.q}"
                </h3>
                <p className="text-white/50 text-sm leading-relaxed" data-id="element-1412">
                  {item.a}
                </p>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* ── SECTION 7: COMPARISON TABLE ── */}
      <section className="relative w-full bg-navy py-24 px-6" data-id="element-1413">
        <SectionDivider data-id="element-1414" />
        <div className="max-w-4xl mx-auto" data-id="element-1415">
          <motion.div className="text-center mb-14" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-60px'
        }} variants={stagger} data-id="element-1416">
            <motion.span variants={fadeUp} className="text-violet-electric text-xs font-semibold tracking-[0.2em] uppercase block mb-4" data-id="element-1417">
              Comparison
            </motion.span>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-bold text-white tracking-tight" data-id="element-1418">
              Enterprise vs. Traditional ERP
            </motion.h2>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-40px'
        }} transition={{
          duration: 0.5
        }} className="rounded-2xl border border-white/[0.06] overflow-hidden" data-id="element-1419">
            <table className="w-full text-sm" data-id="element-1420">
              <thead data-id="element-1421">
                <tr className="border-b border-white/[0.06]" data-id="element-1422">
                  <th className="text-left px-6 py-4 text-white/30 font-medium text-xs uppercase tracking-wider w-1/3" data-id="element-1423"></th>
                  <th className="text-left px-6 py-4 text-white/40 font-medium text-xs uppercase tracking-wider w-1/3" data-id="element-1424">
                    Traditional ERP
                  </th>
                  <th className="text-left px-6 py-4 text-blue-400 font-semibold text-xs uppercase tracking-wider w-1/3 bg-blue-400/[0.04]" data-id="element-1425">
                    Thruline Enterprise
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-1426">
                {[['Discovery method', 'Months of consultant interviews', 'AI reads your documents in days'], ['How it\'s configured', 'Vendor templates with workarounds', 'Built around your actual processes'], ['Implementation time', '6–18 months typical', 'Weeks, not months'], ['Pricing model', 'Unpredictable change orders', 'Fixed quote after Discovery'], ['After go-live', 'Consultant leaves, knowledge goes with them', 'Thruline stays as managed service'], ['When you need changes', 'Re-engage consultants, new SOW', 'Tell Thruline, we handle it'], ['Documentation', 'Delivered once, immediately outdated', 'Self-documenting, always current'], ['Internal team required', 'IT staff or vendor dependency', 'None — Thruline is your team'], ['System evolution', 'Expensive upgrade cycles', 'Continuous alignment with your operation']].map(([label, traditional, enterprise], i) => <tr key={i} className={`border-b border-white/[0.04] ${i % 2 === 0 ? 'bg-white/[0.01]' : ''}`} data-id="element-1427">
                    <td className="px-6 py-4 text-white/50 font-medium" data-id="element-1428">
                      {label}
                    </td>
                    <td className="px-6 py-4 text-white/35" data-id="element-1429">{traditional}</td>
                    <td className="px-6 py-4 text-white/70 bg-blue-400/[0.03]" data-id="element-1430">
                      {enterprise}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </motion.div>
        </div>
      </section>

      {/* ── SECTION 8: TWO WAYS ── */}
      <section className="relative w-full bg-navy-light py-24 px-6" data-id="element-1431">
        <SectionDivider data-id="element-1432" />
        <div className="max-w-4xl mx-auto" data-id="element-1433">
          <motion.div className="text-center mb-14" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-60px'
        }} variants={stagger} data-id="element-1434">
            <motion.span variants={fadeUp} className="text-violet-electric text-xs font-semibold tracking-[0.2em] uppercase block mb-4" data-id="element-1435">
              Two Ways to Work With Thruline
            </motion.span>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-bold text-white tracking-tight mb-4" data-id="element-1436">
              Want it done, or want to own it?
            </motion.h2>
            <motion.p variants={fadeUp} className="text-white/45 text-base max-w-xl mx-auto" data-id="element-1437">
              Both start with the same AI-powered Discovery. The difference is
              who holds the wrench after go-live.
            </motion.p>
          </motion.div>

          <motion.div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-8" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-40px'
        }} variants={stagger} data-id="element-1438">
            {/* Enterprise Card */}
            <motion.div variants={fadeUp} className="rounded-2xl border border-blue-400/20 bg-blue-400/[0.04] p-8" data-id="element-1439">
              <div className="flex items-center gap-3 mb-4" data-id="element-1440">
                <div className="w-10 h-10 rounded-xl bg-blue-400/20 flex items-center justify-center" data-id="element-1441">
                  <BuildingIcon className="w-5 h-5 text-blue-400" data-id="element-1442" />
                </div>
                <div data-id="element-1443">
                  <h3 className="text-lg font-semibold text-white" data-id="element-1444">
                    Thruline Enterprise
                  </h3>
                  <p className="text-blue-400 text-xs font-medium" data-id="element-1445">
                    You're here
                  </p>
                </div>
              </div>
              <p className="text-white/55 text-sm leading-relaxed mb-4" data-id="element-1446">
                A fully custom ERP built and maintained by Thruline. We handle
                the system. You handle the business.
              </p>
              <ul className="space-y-2" data-id="element-1447">
                {['Thruline builds the entire system', 'Thruline operates and maintains', 'Changes handled by Thruline\'s team', 'Best for: companies that want it done'].map((item, i) => <li key={i} className="flex items-start gap-2 text-white/45 text-sm" data-id="element-1448">
                    <CheckIcon className="w-3.5 h-3.5 text-blue-400 mt-0.5 flex-shrink-0" data-id="element-1449" />
                    {item}
                  </li>)}
              </ul>
            </motion.div>

            {/* Core Card */}
            <motion.div variants={fadeUp} className="rounded-2xl border border-white/[0.06] bg-white/[0.03] p-8" data-id="element-1450">
              <div className="flex items-center gap-3 mb-4" data-id="element-1451">
                <div className="w-10 h-10 rounded-xl bg-violet-electric/15 flex items-center justify-center" data-id="element-1452">
                  <ZapIcon className="w-5 h-5 text-violet-electric" data-id="element-1453" />
                </div>
                <h3 className="text-lg font-semibold text-white" data-id="element-1454">
                  Thruline Core
                </h3>
              </div>
              <p className="text-white/55 text-sm leading-relaxed mb-4" data-id="element-1455">
                A custom-developed ERP you own and evolve. Thruline helps you
                plan and build it. You understand it, modify it, and grow it.
              </p>
              <ul className="space-y-2 mb-6" data-id="element-1456">
                {['Thruline builds the first version with you', 'You operate with Thruline advisory', 'You make changes with AI assistance', 'Best for: companies that want to own it'].map((item, i) => <li key={i} className="flex items-start gap-2 text-white/45 text-sm" data-id="element-1457">
                    <CheckIcon className="w-3.5 h-3.5 text-violet-electric mt-0.5 flex-shrink-0" data-id="element-1458" />
                    {item}
                  </li>)}
              </ul>
              <a href="#/solutions/custom" className="inline-flex items-center gap-2 text-violet-electric text-sm font-medium hover:underline" data-id="element-1459">
                Learn about Core
                <ArrowRightIcon className="w-3.5 h-3.5" data-id="element-1460" />
              </a>
            </motion.div>
          </motion.div>

          <motion.p initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="text-white/35 text-sm text-center leading-relaxed max-w-2xl mx-auto" data-id="element-1461">
            Some customers start with Enterprise and transition to Core as
            their team builds confidence. Others start with Core and bring in
            Enterprise for major expansions. The two products complement each
            other.
          </motion.p>
        </div>
      </section>

      {/* ── SECTION 9: BOTTOM CTA ── */}
      <section className="relative w-full py-24 px-6 overflow-hidden" data-id="element-1462">
        <div className="absolute inset-0" style={{
        background: 'linear-gradient(135deg, #0D0B2E 0%, #1A1452 30%, #3b82f6 70%, #818cf8 100%)'
      }} data-id="element-1463" />
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{
        backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E\")"
      }} data-id="element-1464" />

        <motion.div className="relative z-10 max-w-3xl mx-auto text-center" initial={{
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-80px'
      }} transition={{
        duration: 0.6
      }} data-id="element-1465">
          <span className="text-white/60 text-xs font-semibold tracking-[0.2em] uppercase block mb-4" data-id="element-1466">
            Get Started
          </span>
          <h2 className="text-3xl sm:text-5xl font-bold text-white tracking-tight mb-6" data-id="element-1467">
            Your business already knows
            <br data-id="element-1468" />
            how it works. Let us read it.
          </h2>
          <p className="text-white/60 text-lg mb-10 max-w-xl mx-auto" data-id="element-1469">
            Discovery is free. We'll read your documents, map your processes,
            and show you what your custom ERP looks like — before you commit to
            anything.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-1470">
            <a href="https://calendly.com/alex-thruline/learn-about-thruline" className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-white text-navy font-semibold text-sm hover:bg-white/90 transition-all hover:shadow-lg hover:shadow-white/10" data-id="element-1471">
              Start with Discovery
              <ArrowRightIcon className="w-4 h-4" data-id="element-1472" />
            </a>
            <a href="https://calendly.com/alex-thruline/learn-about-thruline" className="inline-flex items-center px-8 py-3.5 rounded-full border border-white/30 text-white font-medium text-sm hover:border-white/60 transition-all" data-id="element-1473">
              Talk to our team
            </a>
          </div>
        </motion.div>
      </section>
    </div>;
}